import { faCaretDown, faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type React from 'react'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { RootState } from '../redux/store'
import type { BankAccountDto, CurrencyDto } from '../types/coreApi-types'
import { canReceivePayoutIn } from '../utils/bankAccountPayout'
import { isAdmin } from '../utils/roleUtils'
import AddBankAccountModal from './AddBankAccountModal'
import DropdownCell from './DropdownCell'
import DropdownMenu from './DropdownMenu'

interface BankAccountSelectorProps {
  label: string
  bankAccount: BankAccountDto | null
  setBankAccount: (bankAccount: BankAccountDto | null) => void
  bankAccountsData: BankAccountDto[] | undefined
  /** Set when choosing where a payout goes: accounts that can't receive this currency can't be picked. */
  payoutCurrency?: CurrencyDto
  disabled?: boolean
  showAddBankAccountOption?: boolean
  className?: string
}

const BankAccountSelector: React.FC<BankAccountSelectorProps> = ({
  label,
  bankAccount,
  setBankAccount,
  bankAccountsData,
  payoutCurrency,
  disabled = false,
  showAddBankAccountOption = true,
  className = '',
}) => {
  const history = useHistory()
  const userInfo = useSelector((state: RootState) => state.session.user)
  const [isAddBankAccountModalOpen, setIsAddBankAccountModalOpen] =
    useState(false)

  const handleAddBankAccountClick = () => {
    if (showAddBankAccountOption) {
      setIsAddBankAccountModalOpen(true)
    } else {
      history.push('/app/bank-accounts')
    }
  }

  return (
    <div className={className}>
      <p className="text-description-subtle mb-2">{label}</p>
      <DropdownMenu
        renderList={
          bankAccountsData && bankAccountsData?.length > 0 ? (
            [
              ...bankAccountsData.map((item, idx) => {
                const cannotReceivePayout = Boolean(
                  payoutCurrency && !canReceivePayoutIn(item, payoutCurrency)
                )
                return (
                  <DropdownCell
                    key={item.id}
                    title={item.name}
                    subtitle={
                      cannotReceivePayout
                        ? `Can't receive ${payoutCurrency?.isoCode} payouts — add this account's IBAN instead`
                        : item.description || ''
                    }
                    onClick={() => setBankAccount(item)}
                    isActive={bankAccount?.id === item.id}
                    disabled={cannotReceivePayout}
                  />
                )
              }),
              // Add "Add new bank account" option at the end
              ...(showAddBankAccountOption
                ? [
                    <DropdownCell
                      key="add-bank-account"
                      title="Add new bank account"
                      subtitle="Click to add a new bank account"
                      onClick={handleAddBankAccountClick}
                      isActive={false}
                      icon={
                        <FontAwesomeIcon
                          icon={faPlusCircle}
                          className="text-brand-accent dark:text-white text-lg"
                        />
                      }
                    />,
                  ]
                : []),
            ]
          ) : (
            <DropdownCell
              title="Add new bank account"
              subtitle="Your account doesn't have any bank accounts, press to add a new one."
              onClick={handleAddBankAccountClick}
              isActive={false}
              icon={
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  className="text-brand-accent dark:text-white text-lg"
                />
              }
            />
          )
        }
      >
        <div
          className={`input text-start flex items-center pl-3 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {bankAccount ? (
            <div className="flex-1">
              <p className="text-sm font-medium">{bankAccount.name}</p>
              {bankAccount.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {bankAccount.description}
                </p>
              )}
            </div>
          ) : (
            <p className="text-description">Select a bank account</p>
          )}
          <div className="absolute right-0 top-0 bottom-0 flex items-center mr-3 text-brand-light-text-description">
            <FontAwesomeIcon icon={faCaretDown} size="sm" />
          </div>
        </div>
      </DropdownMenu>

      {/* Add Bank Account Modal */}
      <AddBankAccountModal
        isOpen={isAddBankAccountModalOpen}
        onClose={() => setIsAddBankAccountModalOpen(false)}
        systemBankAccount={isAdmin(userInfo.role)}
        payoutCurrency={payoutCurrency}
      />
    </div>
  )
}

export default BankAccountSelector
